import { runtimeEnv } from '../../runtimeEnv';

// Per-browser record of the analytics-cookie choice. localStorage, injectable + guarded
// (mirrors anonymousKey.ts): a throw in a private/hardened browser defaults to no consent
// rather than white-screening the app. Not a server record — this is per-browser only.
const KEY = 'eh_cookie_consent';

export type ConsentChoice = 'granted' | 'denied';
export type ConsentRecord = { choice: ConsentChoice; ts: string; policyVersion: string };

type Store = Pick<Storage, 'getItem' | 'setItem'>;

// Returns the stored choice, or null when absent, unreadable, corrupt, or recorded against
// an older policy version (a bump re-prompts, so a stale record reads as "no consent").
// The store is resolved inside the try: with site data blocked, even touching
// window.localStorage throws, and a default parameter would evaluate before the guard.
export function readConsent(
  store?: Store,
  policyVersion: string = runtimeEnv.consentPolicyVersion,
): ConsentRecord | null {
  try {
    const raw = (store ?? localStorage).getItem(KEY);
    if (!raw) return null;
    const rec = JSON.parse(raw) as ConsentRecord;
    if (rec.choice !== 'granted' && rec.choice !== 'denied') return null;
    if (rec.policyVersion !== policyVersion) return null;
    return rec;
  } catch {
    return null;
  }
}

export function writeConsent(
  choice: ConsentChoice,
  store?: Store,
  policyVersion: string = runtimeEnv.consentPolicyVersion,
): ConsentRecord {
  const rec: ConsentRecord = { choice, ts: new Date().toISOString(), policyVersion };
  try {
    (store ?? localStorage).setItem(KEY, JSON.stringify(rec));
  } catch {
    // Private/hardened browser: the choice lives for this session only.
  }
  return rec;
}
